import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { 
  Coins, 
  ArrowDownLeft, 
  ArrowUpRight, 
  Gift, 
  RefreshCw,
  Sparkles,
  Camera,
  MessageSquare,
  Shirt,
  Luggage,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface Transaction {
  id: string;
  type: string;
  amount: number;
  balance_after: number;
  action: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

const ACTION_ICONS: Record<string, typeof Coins> = {
  outfit_generation: Sparkles,
  ootd_rating: Camera,
  virtual_tryon: Shirt,
  agent_chat: MessageSquare,
  clothing_analysis: Camera,
  packing_list: Luggage,
};

const ACTION_LABELS: Record<string, string> = {
  outfit_generation: 'Outfit Generation',
  ootd_rating: 'OOTD Rating',
  virtual_tryon: 'Virtual Try-On',
  agent_chat: 'AI Chat',
  clothing_analysis: 'Clothing Analysis',
  packing_list: 'Packing List',
  welcome_bonus: 'Welcome Bonus',
  daily_login: 'Daily Login Bonus',
  referral_bonus: 'Referral Bonus',
};

function TransactionItem({ transaction }: { transaction: Transaction }) {
  const isPositive = transaction.type === 'purchase' || transaction.type === 'bonus' || transaction.type === 'refund';
  const Icon = transaction.action ? (ACTION_ICONS[transaction.action] || Coins) : 
               transaction.type === 'bonus' ? Gift :
               transaction.type === 'purchase' ? ArrowDownLeft :
               transaction.type === 'refund' ? RefreshCw : ArrowUpRight;

  const getLabel = () => {
    if (transaction.action) {
      return ACTION_LABELS[transaction.action] || transaction.action;
    }
    if (transaction.type === 'purchase') return 'Credit Purchase';
    if (transaction.type === 'bonus') {
      const reason = (transaction.metadata as Record<string, unknown>)?.reason;
      if (typeof reason === 'string') return ACTION_LABELS[reason] || reason;
      return 'Bonus Credits';
    }
    if (transaction.type === 'refund') return 'Refund';
    return 'Credit Spent';
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/50">
      <div className={cn(
        'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
        isPositive 
          ? 'bg-accent text-accent-foreground' 
          : 'bg-destructive/10 text-destructive'
      )}>
        <Icon size={18} />
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-foreground truncate">
          {getLabel()}
        </p>
        <p className="text-xs text-muted-foreground">
          {format(new Date(transaction.created_at), 'MMM d, yyyy • h:mm a')}
        </p>
      </div>
      
      <div className="text-right flex-shrink-0">
        <p className={cn(
          'font-semibold text-sm',
          isPositive ? 'text-primary' : 'text-destructive'
        )}>
          {isPositive ? '+' : '-'}{Math.abs(transaction.amount).toLocaleString()}
        </p>
        <p className="text-[10px] text-muted-foreground">
          Bal: {transaction.balance_after.toLocaleString()}
        </p>
      </div>
    </div>
  );
}

interface TransactionHistoryProps {
  limit?: number;
  showHeader?: boolean;
  className?: string;
}

export function TransactionHistory({ 
  limit, 
  showHeader = true,
  className 
}: TransactionHistoryProps) {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'purchases' | 'spending'>('all');

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const fetchTransactions = async () => {
      setIsLoading(true);
      try {
        let query = supabase
          .from('credit_transactions')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (limit) {
          query = query.limit(limit);
        }

        const { data, error } = await query;
        
        if (error) throw error;
        setTransactions((data || []) as Transaction[]);
      } catch (err) {
        console.error('Failed to fetch transactions:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactions();
  }, [user, limit]);

  const filteredTransactions = transactions.filter(t => {
    if (filter === 'all') return true;
    if (filter === 'purchases') return t.type === 'purchase' || t.type === 'bonus' || t.type === 'refund';
    return t.type === 'spend';
  });

  const totalPurchased = transactions
    .filter(t => t.type === 'purchase' || t.type === 'bonus')
    .reduce((sum, t) => sum + t.amount, 0);
  
  const totalSpent = transactions
    .filter(t => t.type === 'spend')
    .reduce((sum, t) => sum + Math.abs(t.amount), 0);

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className={className}>
      {showHeader && (
        <>
          {/* Stats Summary - Mobile Optimized */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 rounded-xl bg-primary/10 border border-primary/20">
              <div className="flex items-center gap-2 mb-1">
                <ArrowDownLeft size={14} className="text-primary" />
                <span className="text-xs text-muted-foreground">Received</span>
              </div>
              <p className="text-lg font-bold text-primary">
                +{totalPurchased.toLocaleString()}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20">
              <div className="flex items-center gap-2 mb-1">
                <ArrowUpRight size={14} className="text-destructive" />
                <span className="text-xs text-muted-foreground">Spent</span>
              </div>
              <p className="text-lg font-bold text-destructive">
                -{totalSpent.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Filter Tabs - Mobile Friendly */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-1 scrollbar-hide">
            {(['all', 'purchases', 'spending'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  'px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all',
                  filter === f
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                )}
              >
                {f === 'all' ? 'All' : f === 'purchases' ? 'Credits In' : 'Credits Out'}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Transaction List */}
      <div className="space-y-2">
        {filteredTransactions.length === 0 ? (
          <div className="text-center py-8">
            <Coins size={32} className="mx-auto text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">No transactions yet</p>
          </div>
        ) : (
          filteredTransactions.map((transaction) => (
            <TransactionItem key={transaction.id} transaction={transaction} />
          ))
        )}
      </div>
    </div>
  );
}
